// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { FacetModel } from "@insite/client-framework/Types/ApiModels";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import Checkbox, {
    CheckboxPresentationProps,
    StyleProps as CheckboxStyleProps,
} from "@insite/mobius/Checkbox/Checkbox";
import CheckboxGroup, { CheckboxGroupProps } from "@insite/mobius/CheckboxGroup/CheckboxGroup";
import Hidden from "@insite/mobius/Hidden";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import omitMultiple from "@insite/mobius/utilities/omitMultiple";
import customProductListFiltersAccordionSectionStyle, {
    customProprietiesInProductListFiltersAccordionSectionStyle,
} from "@rugby/Overrides/Widgets/ProductList/styles/ProductListFilterAccordionSection.style";
import { ellipsis } from "@rugby/XDL/Utilities";
import { styledScrollbar } from "@rugby/XDL/Utilities/SharedStyles";
import React, { FC, useState } from "react";
import { css } from "styled-components";

export interface ProductListFilterAccordionSectionStyles {
    accordionSection?: AccordionSectionPresentationProps;
    checkBoxGroup?: CheckboxGroupProps;
    checkBox?: CheckboxPresentationProps;
    checkBoxSelected?: CheckboxPresentationProps;
    seeAllLink?: LinkPresentationProps;
    seeLessLink?: LinkPresentationProps;

    // <XNG-Changes>
    attributeLabel?: TypographyPresentationProps;
    attributeCountLabel?: TypographyPresentationProps;
    // </XNG-Changes>
}

export const productListFilterAccordionSectionStyles: ProductListFilterAccordionSectionStyles = {
    accordionSection: {
        panelProps: {
            css: css`
                overflow-y: auto;
                overflow-x: hidden;
                max-height: 350px;
            `,
        },
    },
    checkBoxGroup: {
        css: css`
            width: 100%;
            &:not(:last-child) {
                padding-bottom: 5px;
            }
        `,
    },
    checkBox: {
        css: css<CheckboxStyleProps>`
            align-items: flex-start;
            span[role="checkbox"] {
                ${({ _sizeVariant }) =>
                    _sizeVariant === "small"
                        ? css`
                              margin-top: 5px;
                          `
                        : css`
                              margin-top: 4px;
                          `}
            }
        `,
        typographyProps: {
            css: css`
                word-break: break-all;
            `,
        },
    },
    checkBoxSelected: {
        css: css<CheckboxStyleProps>`
            align-items: flex-start;
            span[role="checkbox"] {
                ${({ _sizeVariant }) =>
                    _sizeVariant === "small"
                        ? css`
                              margin-top: 5px;
                          `
                        : css`
                              margin-top: 4px;
                          `}
            }
        `,
        typographyProps: {
            css: css`
                word-break: break-all;
            `,
            weight: "bold",
        },
    },
    seeAllLink: {
        css: css`
            padding-top: 5px;
        `,
    },
    seeLessLink: {
        css: css`
            padding-top: 5px;
        `,
    },
};

export interface Props {
    title: string;
    facets: FacetModel[];
    onChangeFacet: (facet: FacetModel) => void;
    showMoreLimit: number;
    extendedStyles?: ProductListFilterAccordionSectionStyles;
    expandByDefault: boolean;
}

const ProductListFiltersAccordionSection: FC<Props> = ({
    title,
    facets,
    onChangeFacet,
    showMoreLimit,
    expandByDefault,
    extendedStyles,
}) => {
    const originalStyle = mergeToNew(
        productListFilterAccordionSectionStyles,
        customProductListFiltersAccordionSectionStyle,
    );
    // <XNG-Changes> get style from styles/ProductListFilterAccordionSection.style.ts
    const customStyle = omitMultiple(originalStyle, customProprietiesInProductListFiltersAccordionSectionStyle);

    const [styles] = React.useState(() => mergeToNew(customStyle, extendedStyles));
    // </XNG-Changes>

    const [expanded, setExpanded] = useState(false);

    const limitedFacets = expanded ? facets : facets?.slice(0, showMoreLimit);
    const anySelected = facets.find(f => f.selected) !== undefined;

    // <XNG-Changes> render items as Checkbox items
    // withLimitation: to show/hide "see more" link
    const renderAsCheckboxItems = (withLimitation: boolean) => {
        let limitedFacets;
        if (withLimitation) {
            limitedFacets = expanded ? facets : facets?.slice(0, showMoreLimit);
        } else {
            limitedFacets = facets;
        }

        return (
            <>
                {limitedFacets?.map(facet => (
                    <CheckboxGroup key={facet.id} {...styles.checkBoxGroup}>
                        <Checkbox
                            {...(facet.selected ? styles.checkBoxSelected : styles.checkBox)}
                            checked={facet.selected}
                            onChange={() => onChangeFacet(facet)}
                            data-test-selector={`facetFilter${facet.id}`}
                        >
                            <StyledWrapper
                                css={css`
                                    display: flex;
                                    justify-content: space-between;
                                `}
                            >
                                <Typography {...styles.attributeLabel}>{facet.name}</Typography>
                                <Typography {...styles.attributeCountLabel}>
                                    {facet.count !== -1 && `${facet.count}`}
                                </Typography>
                            </StyledWrapper>
                        </Checkbox>
                    </CheckboxGroup>
                ))}
                {facets &&
                    withLimitation &&
                    facets.length > showMoreLimit &&
                    (expanded ? (
                        <Link {...styles.seeLessLink} onClick={() => setExpanded(false)}>
                            {translate("See Less")}
                        </Link>
                    ) : (
                        <Link {...styles.seeAllLink} onClick={() => setExpanded(true)}>
                            {translate("See All")}
                        </Link>
                    ))}
            </>
        );
    };
    // </XNG-Changes>

    // <XNG-Changes> render items as AccordionSection
    // for Mobile style
    const renderWithAccordionWrapper = (
        <ManagedAccordionSection
            title={title}
            {...customProductListFiltersAccordionSectionStyle.accordionSection}
            initialExpanded={expandByDefault || anySelected}
        >
            {renderAsCheckboxItems(true)}
        </ManagedAccordionSection>
    );
    // </XNG-Changes>

    // <XNG-Changes> render items as Checkbox List
    // for Desktop style
    const renderAsCheckboxListWrapper = (
        <StyledWrapper {...customProductListFiltersAccordionSectionStyle.checkboxListStyle.wrapper}>
            {title && (
                <Typography
                    {...(customProductListFiltersAccordionSectionStyle.checkboxListStyle
                        .title as TypographyPresentationProps)}
                >
                    {title}
                </Typography>
            )}
            <StyledWrapper {...customProductListFiltersAccordionSectionStyle.container}>
                {renderAsCheckboxItems(false)}
            </StyledWrapper>
        </StyledWrapper>
    );

    // <XNG-Changes> Desktop & Mobile structure
    return (
        <StyledWrapper data-test-selector="productListFilterAccordionSection_customWrapper">
            {renderWithAccordionWrapper}
        </StyledWrapper>
    );
    // </XNG-Changes>
};

export default ProductListFiltersAccordionSection;
